import unionBy from 'lodash/unionBy';
import orderBy from 'lodash/orderBy';
import get from 'lodash/get';
import head from 'lodash/head';

import { openChatPopup, closeChatPopup, ChatType } from 'modules/chat';

import { AccountType } from 'modules/accounts';
import { getTalentByAccountId } from 'modules/talents';
import { getAgent } from 'modules/agents';
import { getProducer } from 'modules/producers';
import {
  fetchGlobalMessagesThread,
  fetchUnreadGlobalMessagesThreads,
  postGlobalMessagesThread,
  postGlobalMessagesThreadUnreadStatus,
  searchGlobalMessagesThreadsByName,
  markAllMessagesAsRead as apiMarkAllMessagesAsRead,
} from './api';
import {
  findGlobalMessagingThreadWith,
  globalMessagingChatFilter,
  globalMessagingChatId,
} from './services';
import {
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';

export const loadGlobalMessagingThread = createAsyncThunk(
  'globalMessagingThreads/loadThread',
  async ({ accountId, sort, limit }) =>
    await fetchGlobalMessagesThread(accountId, sort, limit),
);

export const createGlobalMessagingThread = createAsyncThunk(
  'globalMessagingThreads/createThread',
  async oppositeAccountData =>
    await postGlobalMessagesThread(oppositeAccountData),
);

export const markGlobalMessagingThreadUnread = createAsyncThunk(
  'globalMessagingThreads/markUnread',
  async ({ threadId, oppositeAccountId }) =>
    await postGlobalMessagesThreadUnreadStatus(threadId, oppositeAccountId),
);

export const searchGlobalMessagingThreads = createAsyncThunk(
  'globalMessagingThreads/search',
  async ({ query, accountId, filteredByUnread }) =>
    await searchGlobalMessagesThreadsByName(query, accountId, filteredByUnread),
);

export const loadUnreadGlobalMessagingThreads = createAsyncThunk(
  'globalMessagingThreads/loadUnreadThreads',
  async ({ accountId, unreadMessagesCount }) =>
    await fetchUnreadGlobalMessagesThreads(accountId, unreadMessagesCount),
);

export function openGlobalMessagingChatPopup(
  oppositeAccountId,
  oppositeAccountType,
) {
  return openChatPopup(
    globalMessagingChatId(oppositeAccountId),
    ChatType.GLOBAL,
    { oppositeAccountId, oppositeAccountType },
    globalMessagingChatFilter(oppositeAccountId),
  );
}

export function closeGlobalMessagingChatPopup(accountId) {
  return closeChatPopup(globalMessagingChatFilter(accountId));
}

export const markAllMessagesAsRead = createAsyncThunk(
  'globalMessagingThreads/markAllMessagesRead',
  async _ => await apiMarkAllMessagesAsRead(),
);

const globalMessageThreadsSlice = createSlice({
  name: 'globalMessagingThreads',
  initialState: {
    threads: [],
    searchedThreads: [],
    loadingThreads: false,
  },
  reducers: {
    clearSearchedGlobalMessagingThreads(state) {
      state.searchedThreads = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadGlobalMessagingThread.pending, (state, _) => {
        state.loadingThreads = true;
      })
      .addCase(loadGlobalMessagingThread.fulfilled, (state, action) => {
        state.loadingThreads = false;
        state.threads = unionBy(action.payload.data, state.threads, 'id');
      })
      .addCase(createGlobalMessagingThread.fulfilled, (state, action) => {
        state.threads = [...state.threads, action.payload.data];
      })
      .addCase(searchGlobalMessagingThreads.pending, (state, _) => {
        state.loadingThreads = true;
      })
      .addCase(searchGlobalMessagingThreads.fulfilled, (state, action) => {
        state.loadingThreads = false;
        state.searchedThreads = action.payload.data;
      })
      .addCase(searchGlobalMessagingThreads.rejected, (state, _) => {
        state.loadingThreads = false;
      })
      .addCase(loadUnreadGlobalMessagingThreads.pending, (state, _) => {
        state.loadingThreads = true;
      })
      .addCase(loadUnreadGlobalMessagingThreads.fulfilled, (state, action) => {
        state.loadingThreads = false;
        state.searchedThreads = action.payload.data;
      })
      .addCase(loadUnreadGlobalMessagingThreads.rejected, (state, _) => {
        state.loadingThreads = false;
      })
      .addCase(markAllMessagesAsRead.pending, (state, _) => {
        state.loadingThreads = true;
      })
      .addCase(markAllMessagesAsRead.fulfilled, (state, _) => {
        state.loadingThreads = false;
      })
      .addCase(markAllMessagesAsRead.rejected, (state, _) => {
        state.loadingThreads = false;
      });
  },
  selectors: {
    getGlobalMessagingThreads: state => state.threads,
    getLoadingThreads: state => state.loadingThreads,
    getSearchedThreads: state => state.searchedThreads,
  },
});

export const reducer = globalMessageThreadsSlice.reducer;
export const {
  clearSearchedGlobalMessagingThreads,
} = globalMessageThreadsSlice.actions;
export const {
  getGlobalMessagingThreads,
  getLoadingThreads,
  getSearchedThreads,
} = globalMessageThreadsSlice.selectors;

export const getGlobalMessagingThreadsSortedByLatestMessages = createSelector(
  getGlobalMessagingThreads,
  threads =>
    orderBy(threads, [thread => thread.updatedAt.toLowerCase()], 'desc'),
);

export const getLatestGlobalMessagingThread = createSelector(
  getGlobalMessagingThreadsSortedByLatestMessages,
  head,
);

export const getGlobalMessagingThreadWith = (accountId, ofType) =>
  createSelector(
    getGlobalMessagingThreads,
    threads => findGlobalMessagingThreadWith(accountId, ofType, threads),
  );

export const getOppositeUser = (accountId, accountType) => state => {
  const threads = getGlobalMessagingThreads(state);
  const thread = findGlobalMessagingThreadWith(accountId, accountType, threads);
  const accountInfo = get(thread, `${accountType}Account.${accountType}`);
  if (accountInfo !== undefined) {
    return accountInfo;
  }
  if (AccountType.isAgent(accountType)) {
    return getAgent(state);
  }
  if (AccountType.isTalent(accountType)) {
    return getTalentByAccountId(accountId)(state);
  }
  if (AccountType.isProducer(accountType)) {
    return getProducer(state);
  }
};
